import { TimezoneMap, TimezoneNames } from '../constants/timezones';
import { DateFormatSetting, StartOfWeekFormatSetting, TimeFormatSetting, TimezoneSetting } from '../models/graphql';
import { enumKeys } from './enumEnumerator';
import { PostOnDateObject, ScheduledDateForm } from '../models/post-editor';
import {
  addDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  isBefore,
  isValid,
  parse,
  parseISO,
  setHours,
  setMinutes,
  startOfDay
} from 'date-fns';
import { utcToZonedTime, toDate, zonedTimeToUtc } from 'date-fns-tz';
import { DateTimeStringFormat, DateTimeStringFormatWithZone } from '../constants/time-formats';
import { TDateTimeString, TDateTimeWithoutSecondsString } from '../models/date';

export const formatTimeDiffShort = (timestamp: number, compareTo?: number): string => {
  const oneHour = 60;
  const oneDay = 24;
  const qtyDaysPerWeek = 7;
  const qtyWeeksPerMonth = 4;
  const qtyMonthsPerYear = 12;

  // Ensure timestamps are in milliseconds.
  if (timestamp < 9000000000) {
    timestamp *= 1000;
  }
  if (compareTo && compareTo < 9000000000) {
    compareTo *= 1000;
  }

  const timestampToCompare = (compareTo ?? new Date().getTime()) as number;
  const dateToCompare = new Date(timestampToCompare);
  const firstDate = new Date(timestamp);
  const isFirstDateBefore = isBefore(firstDate, dateToCompare);

  const diffInMinutes = isFirstDateBefore
    ? differenceInMinutes(dateToCompare, firstDate)
    : differenceInMinutes(firstDate, dateToCompare);

  if (diffInMinutes < oneHour) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = isFirstDateBefore
    ? differenceInHours(dateToCompare, firstDate)
    : differenceInHours(firstDate, dateToCompare);

  if (diffInHours < oneDay) {
    return `${diffInHours}h`;
  }

  const diffInDays = isFirstDateBefore
    ? differenceInDays(dateToCompare, firstDate)
    : differenceInDays(firstDate, dateToCompare);

  if (diffInDays < qtyDaysPerWeek) {
    return `${diffInDays}d`;
  }

  // Its necessary check for months first because four weeks usually is not enough the be a whole month
  const diffInMonths = isFirstDateBefore
    ? differenceInMonths(dateToCompare, firstDate)
    : differenceInMonths(firstDate, dateToCompare);

  if (diffInMonths < qtyMonthsPerYear && diffInMonths !== 0) {
    return `${diffInMonths}m`;
  }

  const diffInWeeks = isFirstDateBefore
    ? differenceInWeeks(dateToCompare, firstDate)
    : differenceInWeeks(firstDate, dateToCompare);
  if (diffInWeeks <= qtyWeeksPerMonth) {
    return `${diffInWeeks}w`;
  }

  const diffInYears = isFirstDateBefore
    ? differenceInYears(dateToCompare, firstDate)
    : differenceInYears(firstDate, dateToCompare);

  return `${diffInYears}y`;
};

export const formatHour = (timeFormat: TimeFormatSetting, hour: number) => {
  const formatHourString = timeFormat === TimeFormatSetting.HOUR_12 ? 'h a' : 'HH:mm';
  const dateWithHour = new Date().setHours(hour, 0);
  return format(dateWithHour, formatHourString);
};

export const formatTimezoneToDisplay = (tz: TimezoneSetting): string => {
  const mappedTz = TimezoneMap[tz] || '';
  return mappedTz.replace('_', ' ');
};

export const convertTimezoneNameToEnum = (name: TimezoneNames): string | null => {
  for (const key of enumKeys(TimezoneSetting)) {
    if (TimezoneMap[key] === name) {
      return key;
    }
  }

  return null;
};

export const convertTimezoneEnumToName = (tz: TimezoneSetting): TimezoneNames | null => {
  return TimezoneMap[tz] || null;
};

export const convertDateFormatSettingToString = (format: DateFormatSetting): string => {
  switch (format) {
    case DateFormatSetting.DAY_MONTH_YEAR:
      return 'dd/mm/yy';
    case DateFormatSetting.MONTH_DAY_YEAR:
    default:
      return 'mm/dd/yy';
  }
};

export const getAmPm = (date: Date): 'am' | 'pm' => (date.getHours() < 12 ? 'am' : 'pm');
export const convert24to12h = (hour: number): number => {
  if (hour === 0) {
    return 12;
  } else if (hour > 12) {
    return hour - 12;
  }
  return hour;
};
export const convertAmPmTo24h = (hour: string, format: 'am' | 'pm'): string => {
  if (format === 'am') {
    return hour === `12` ? '00' : hour;
  } else {
    const hourNumeric = +`${hour}`;
    const convertedHour = hourNumeric < 12 ? hourNumeric + 12 : hourNumeric;
    return `${convertedHour}`;
  }
};

export const convertDateToPostOnObject = (date: Date, timeFormat: TimeFormatSetting): ScheduledDateForm => {
  const ampm = timeFormat === TimeFormatSetting.HOUR_12 ? getAmPm(date) : undefined;
  const minuteVal = date.getMinutes();
  const minute = minuteVal < 10 ? `0${minuteVal}` : `${minuteVal}`;
  const hourVal = date.getHours();
  const hour = ampm !== undefined ? `${convert24to12h(hourVal)}`.padStart(2, '0') : `${hourVal}`.padStart(2, '0');
  return {
    date,
    ampm,
    hour,
    minute
  };
};

export const convertScheduledDateFormToZonedDate = (date: ScheduledDateForm, timezone: TimezoneNames): Date => {
  const { date: dateObj, hour, minute, ampm } = date;
  const hours = ampm ? convertAmPmTo24h(hour, ampm) : hour;
  const parsedHours = parseInt(hours);
  const parsedMinutes = parseInt(minute);
  const strDate = dateObj.toLocaleDateString('en-US'); // 3/7/2022
  const strDateArr = strDate.split('/'); // dd/mm/yyyy
  const strDateStr = `${strDateArr[2]}-${strDateArr[0].padStart(2, '0')}-${strDateArr[1].padStart(2, '0')}`;
  const hoursString = `${parsedHours}`.padStart(2, '0');
  const minutesString = `${parsedMinutes}`.padStart(2, '0');
  const stringDate = `${strDateStr}T${hoursString}:${minutesString}:00`;
  const parsedDate = toDate(stringDate, { timeZone: timezone });
  return utcToZonedTime(parsedDate, timezone);
};

export const convertPostOnObjectToTimeZoned = (
  date: PostOnDateObject,
  timezone: TimezoneNames
): { zonedDate: Date; utcDate: Date } => {
  const { date: dateObj, time } = date;
  const strDate = dateObj.toLocaleDateString('en-US'); // 3/7/2022
  const strDateArr = strDate.split('/'); // dd/mm/yyyy
  const strDateStr = `${strDateArr[2]}-${strDateArr[0].padStart(2, '0')}-${strDateArr[1].padStart(2, '0')}`;
  const hours = `${time.getHours()}`.padStart(2, '0');
  const minutes = `${time.getMinutes()}`.padStart(2, '0');
  const stringDate = `${strDateStr}T${hours}:${minutes}:00`;
  const utcDate = zonedTimeToUtc(stringDate, timezone);
  const parsedDate = toDate(stringDate, { timeZone: timezone });
  const zonedDate = utcToZonedTime(parsedDate, timezone);
  return { zonedDate, utcDate };
};

export const mapDayTimePositionToDateString = (
  targetDay: number,
  targetHour: number,
  targetMinutes: number,
  startDayOfWeek: StartOfWeekFormatSetting,
  startDate: Date
): string => {
  // Adjust the targetDay based on the startDayOfWeek setting
  let adjustedDay = targetDay - 1; // Default adjustment for 0-based day

  if (startDayOfWeek === StartOfWeekFormatSetting.MONDAY) {
    adjustedDay = adjustedDay % 7;
  }

  // Start from the beginning of startDate, then add the adjusted days and set the desired hour
  const targetDate = setMinutes(setHours(addDays(startOfDay(startDate), adjustedDay), targetHour), targetMinutes);

  return format(targetDate, DateTimeStringFormat);
};

export const convertUtcToTimezonedDate = (dateString: TDateTimeString, timezone: TimezoneNames): TDateTimeString => {
  // We use XXX to maintain strict compliance to ISO 8601
  const date = parse(dateString + 'Z', DateTimeStringFormatWithZone, new Date());

  // TODO this part dos not seem ok, we should throw an error, not return the original string.
  if (!isValid(date) || !date) {
    throw new Error(`dateString: ${dateString} passed as date is invalid!`);
  }

  const zonedDate = utcToZonedTime(date, timezone);
  return format(zonedDate, DateTimeStringFormat) as TDateTimeString;
};

export const isDateBefore = (dates: string[] | string, dateToCompare: Date): boolean => {
  const datesArray = Array.isArray(dates) ? dates : [dates];
  return datesArray.some(date => isBefore(parseISO(date), dateToCompare));
};

// Type guard for TDateTimeWithoutSecondsString
// Checks if the given string matches the format 'yyyy-MM-dd HH:mm'
export const isValidDateTimeWithoutSecondsString = (dateTime: string): dateTime is TDateTimeWithoutSecondsString => {
  const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
  return regex.test(dateTime);
};
